import { Injectable, OnDestroy } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { BehaviorSubject, Subscription } from 'rxjs';

export interface NetworkStatus {
  connected: boolean;
  connectionType: 'wifi' | 'cellular' | 'ethernet' | 'none' | 'unknown';
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class NetworkService implements OnDestroy {
  private networkStatus = new BehaviorSubject<NetworkStatus>({
    connected: true,
    connectionType: 'unknown',
    timestamp: Date.now()
  });
  
  private nativeEventSubscription?: () => void;
  private browserEventSubscriptions: Subscription[] = [];
  private pollingInterval?: any;

  constructor(private platform: Platform) {
    this.initializeNetworkMonitoring();
  }

  get networkStatus$() {
    return this.networkStatus.asObservable();
  }

  get currentStatus() {
    return this.networkStatus.value;
  }

  private async initializeNetworkMonitoring() {
    // Get initial status
    await this.checkNetworkStatus();
    
    if (Capacitor.isNativePlatform()) {
      if (Capacitor.getPlatform() === 'android') {
        this.setupAndroidNetworkListener();
      } else if (Capacitor.getPlatform() === 'ios') {
        this.setupIOSNetworkListener();
      }
    } else {
      this.setupBrowserNetworkListener();
    }
    
    // Add polling as fallback (every 3 seconds for Android)
    if (Capacitor.getPlatform() === 'android') {
      this.startPolling();
    }
  }

  private setupAndroidNetworkListener() {
    // Listen for native Android broadcast events
    window.addEventListener('nativeNetworkChange', this.handleNativeNetworkEvent.bind(this));
    
    // Store cleanup function
    this.nativeEventSubscription = () => {
      window.removeEventListener('nativeNetworkChange', this.handleNativeNetworkEvent.bind(this));
    };
  }

  private setupIOSNetworkListener() {
    // For iOS, you might want to use Capacitor Network plugin
    // or implement similar native bridge
    this.setupBrowserNetworkListener();
    
    // Note: For iOS, consider implementing a similar native solution
    // or use polling since Capacitor Network has similar limitations
  }

  private setupBrowserNetworkListener() {
    const onlineSub = window.addEventListener('online', () => {
      this.updateNetworkStatus({
        connected: true,
        connectionType: this.detectBrowserConnectionType(),
        timestamp: Date.now()
      });
    });

    const offlineSub = window.addEventListener('offline', () => {
      this.updateNetworkStatus({
        connected: false,
        connectionType: 'none',
        timestamp: Date.now()
      });
    });

    // Store references for cleanup
    this.browserEventSubscriptions.push(onlineSub as any);
    this.browserEventSubscriptions.push(offlineSub as any);
  }

  private handleNativeNetworkEvent(event: any) {
    const detail = event.detail;
    this.updateNetworkStatus({
      connected: detail.connected,
      connectionType: detail.connectionType as any,
      timestamp: Date.now()
    });
  }

  private async checkNetworkStatus() {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
      // For Android, we rely on the native events
      // Initial status will be updated when first event arrives
      return;
    }
    
    // For web/iOS, check navigator
    const connected = navigator.onLine;
    const connectionType = connected ? this.detectBrowserConnectionType() : 'none';
    
    this.updateNetworkStatus({
      connected,
      connectionType,
      timestamp: Date.now()
    });
  }

  private detectBrowserConnectionType(): 'wifi' | 'cellular' | 'ethernet' | 'unknown' {
    const connection = (navigator as any).connection || 
                       (navigator as any).mozConnection || 
                       (navigator as any).webkitConnection;
    
    if (connection) {
      switch (connection.type || connection.effectiveType) {
        case 'wifi':
        case 'ethernet':
          return connection.type;
        case 'cellular':
        case '2g':
        case '3g':
        case '4g':
        case '5g':
          return 'cellular';
        default:
          return 'unknown';
      }
    }
    
    return 'unknown';
  }

  private startPolling() {
    // Poll every 3 seconds as fallback for Android
    this.pollingInterval = setInterval(async () => {
      await this.checkNetworkStatus();
    }, 3000);
  }

  private updateNetworkStatus(status: NetworkStatus) {
    const current = this.networkStatus.value;
    
    // Only update if status actually changed
    if (current.connected !== status.connected || 
        current.connectionType !== status.connectionType) {
      this.networkStatus.next(status);
    }
  }

  async forceNetworkCheck() {
    await this.checkNetworkStatus();
  }

  ngOnDestroy() {
    // Cleanup native event listener
    if (this.nativeEventSubscription) {
      this.nativeEventSubscription();
    }

    // Cleanup browser event listeners
    this.browserEventSubscriptions.forEach(sub => {
      if (sub && typeof sub === 'function') {
        // sub(); // If it's a cleanup function
      } else if (sub && typeof sub.unsubscribe === 'function') {
        sub.unsubscribe();
      }
    });

    // Clear polling interval
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  }
}